import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import EditableAutocomplete from "../../../components/EditableAutocomplete.js";
import EditableField from "../../../components/EditableField.js";
import EditButton from "../../../components/EditButton.js";
import { DASH } from "../../../lib/constants.js";
import { formatString } from "../../../lib/formatString.js";

const capitalizeFirstLetter = (string = "") =>
  string.charAt(0).toUpperCase() + string.slice(1);

export default function Relations({
  currentName,
  handleChange,
  handleDelete,
  isEditing,
  locales,
  predictiveList = [],
  relations,
  runId,
}) {
  return (
    <List>
      {relations.map(({ description, id, object = {} }, index) => {
        const {
          host,
          id: otherId,
          name: otherName,
          relationships: [{ description: otherDescription } = {}] = [],
        } = object || {};
        const collapsedCurrentName = currentName?.replaceAll(" ", "");
        const collapsedOtherName = otherName?.replaceAll(" ", "");
        const namePlaceholder = locales["character.relation.newCharacter"];

        return (
          <ListItem
            alignItems="flex-start"
            disableGutters
            key={index}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box
              id={`character-relation-${collapsedOtherName}`}
              display="flex"
              width="100%"
            >
              <ListItemAvatar
                id={`character-relation-${collapsedOtherName}-avatar`}
              >
                <Avatar
                  alt={otherName}
                  src={host?.user?.imageUrl}
                  variant="rounded"
                />
              </ListItemAvatar>
              <Box
                id={`character-relation-${collapsedOtherName}-text`}
                width="100%"
              >
                <ListItemText
                  disableTypography
                  primary={
                    <EditableAutocomplete
                      disabledOptions={(option) =>
                        relations.some(({ object }) => object?.id === option.id)
                      }
                      id={`${collapsedCurrentName}-about-${collapsedOtherName}-title`}
                      isEditing={!id}
                      label={locales["character.relation.name"]}
                      onChange={(event, value) =>
                        handleChange({
                          field: "object",
                          index,
                          value: value || {},
                        })
                      }
                      optionLabel={(option) => option.name || ""}
                      options={predictiveList}
                      route={`/${runId}/plots/characters/${otherId}`}
                      state={{ id: otherId }}
                      text={formatString(locales["character.relation.about"], [
                        currentName,
                        otherName || namePlaceholder,
                      ])}
                      value={
                        predictiveList?.find(
                          (option) => option.id === otherId
                        ) || {}
                      }
                    />
                  }
                  secondary={
                    <EditableField
                      id={`${collapsedCurrentName}-about-${collapsedOtherName}-text`}
                      isEditing={isEditing}
                      label={formatString(locales["character.relation.about"], [
                        currentName || capitalizeFirstLetter(namePlaceholder),
                        otherName || namePlaceholder,
                      ])}
                      multiline
                      onChange={({ target }) =>
                        handleChange({
                          field: "description",
                          index,
                          value: target.value,
                        })
                      }
                      text={description}
                      value={description ?? ""}
                      variant="body2"
                    />
                  }
                  sx={{ display: "flex", flexDirection: "column" }}
                />
                <ListItemText
                  disableTypography
                  primary={
                    otherName &&
                    !isEditing && (
                      <Typography
                        id={`${collapsedOtherName}-about-${collapsedCurrentName}-title`}
                        variant="body1"
                      >
                        {formatString(locales["character.relation.about"], [
                          otherName,
                          currentName,
                        ])}
                      </Typography>
                    )
                  }
                  secondary={
                    <EditableField
                      id={`${collapsedOtherName}-about-${collapsedCurrentName}-text`}
                      isEditing={isEditing}
                      label={formatString(locales["character.relation.about"], [
                        otherName || capitalizeFirstLetter(namePlaceholder),
                        currentName || namePlaceholder,
                      ])}
                      multiline
                      onChange={({ target }) =>
                        handleChange({
                          field: "object.relationships[0].description",
                          index,
                          value: target.value,
                        })
                      }
                      text={otherDescription ?? DASH}
                      value={otherDescription ?? ""}
                      variant="body2"
                    />
                  }
                  sx={{ display: "flex", flexDirection: "column" }}
                />
              </Box>
            </Box>
            {isEditing && (
              <EditButton
                ariaLabel={locales["character.relation.delete"]}
                isEditing={isEditing}
                onClick={() => handleDelete(index)}
                primaryIcon={<DeleteOutlineIcon />}
              />
            )}
          </ListItem>
        );
      })}
    </List>
  );
}
